import { html, LitElement } from 'lit'
import { property, state } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
@safeCustomElement('mdc-password-input')
export class MdcPasswordInput extends LitElement {
  static formAssociated = true

  @property({ type: Boolean, attribute: 'show-password' }) showPassword = false
  @property({ type: String }) label = ''
  @property({ type: String }) placeholder = ''
  @property({ type: String }) hint = ''
  @property({ type: Boolean }) disabled = false
  @property({ type: Boolean }) required = false
  @property({ type: String, attribute: 'validation' }) validationStr = ''
  @property({ type: String }) value = ''
  @property({ type: String }) name = ''

  @state() private internalShowPassword = false
  private _internals!: ElementInternals
  private _boundHandleInnerChange = this._handleInnerChange.bind(this)

  constructor() {
    super()
    this._internals = this.attachInternals()
  }

  private get validation() {
    if (!this.validationStr) return undefined
    try {
      return JSON.parse(this.validationStr)
    } catch {
      return undefined
    }
  }

  private get actualShowPassword() {
    return this.showPassword !== undefined ? this.showPassword : this.internalShowPassword
  }

  connectedCallback() {
    super.connectedCallback()
    this._internals.setFormValue(this.value || null)
    this.addEventListener('change', this._boundHandleInnerChange)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListener('change', this._boundHandleInnerChange)
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties)
    if (changedProperties.has('value') || changedProperties.has('name')) {
      this._internals.setFormValue(this.value || null)
      this._updateValidity()
    }
  }

  private _handleInnerChange(e: Event) {
    const detail = (e as CustomEvent).detail
    if (detail?.value !== undefined) {
      this.value = detail.value
      this._internals.setFormValue(this.value || null)
      this._updateValidity()
    }
  }

  private _updateValidity() {
    const textField = this.shadowRoot?.querySelector('mdc-text-field')
    const input = textField?.shadowRoot?.querySelector('input')
    if (input && textField) {
      this._internals.setValidity(
        input.validity,
        input.validationMessage,
        textField as unknown as HTMLElement
      )
    }
  }

  formResetCallback() {
    this.value = ''
    this._internals.setFormValue(null)
  }

  formDisabledCallback(disabled: boolean) {
    this.disabled = disabled
  }

  formStateRestoreCallback(state: string) {
    this.value = state
    this._internals.setFormValue(this.value || null)
  }

  render() {
    const buttonOptions = {
      type: 'iconToggle',
      options: {
        checked: this.actualShowPassword,
        type: 'tertiary',
        size: 'md',
        onIcon: 'eye-slash',
        offIcon: 'eye',
      },
    }

    return html`
			<mdc-text-field
				type=${this.actualShowPassword ? 'text' : 'password'}
				label=${this.label}
				placeholder=${this.placeholder}
				hint=${this.hint}
				?disabled=${this.disabled}
				?required=${this.required}
				validation=${this.validation ? JSON.stringify(this.validation) : ''}
				value=${this.value}
				name=${this.name}
				button=${JSON.stringify(buttonOptions)}
			></mdc-text-field>
		`
  }
}
